/**
 * Versioned local save schema with validation, migration, and corruption recovery.
 * Compact EditGraph JSON only — no media blobs.
 */
import {
  deserializeEditGraph,
  scoreEdit,
  validateEditGraphAgainstPack,
  type ScoreBreakdown,
} from "@desk/core";
import type {
  AudienceResult,
  GameState,
  PublishRewards,
  PublishSnapshot,
  StoreItemId,
  UnlockId,
} from "../types.js";
import {
  DEFAULT_DRAFT_SLOTS,
  DEFAULT_PORTFOLIO_SLOTS,
  MAX_HOME_INTRO_VERSION,
  SAVE_SCHEMA_VERSION,
  STARTING_UNLOCKS,
} from "../types.js";
import { createInitialGameState, isCurrentEditValid } from "../reducer.js";
import type { PackId } from "../../content/registry.js";
import { PACK_REGISTRY, SHIFT_PACK_ORDER } from "../../content/registry.js";
import { STORE_CATALOG, sanitizeEquippedTheme } from "../store-catalog.js";

export const SAVE_STORAGE_KEY = "desk.game.save.v1";
/** Soft ceiling — oversized JSON is treated as corruption. */
export const MAX_SAVE_JSON_CHARS = 400_000;

const KNOWN_UNLOCKS = new Set<string>([
  "cut",
  "frame",
  "text",
  "sound",
  "cast",
  "captionDeep",
  "soundDeep",
]);
const KNOWN_STORE = new Set<string>(STORE_CATALOG.map((i) => i.id));

export interface PersistedSaveV1 {
  schemaVersion: typeof SAVE_SCHEMA_VERSION;
  game: GameState;
}

export interface StorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Deterministic in-memory adapter for tests. */
export class MemoryStorageAdapter implements StorageAdapter {
  private readonly map = new Map<string, string>();
  getItem(key: string): string | null {
    return this.map.has(key) ? this.map.get(key)! : null;
  }
  setItem(key: string, value: string): void {
    this.map.set(key, value);
  }
  removeItem(key: string): void {
    this.map.delete(key);
  }
}

export function browserStorageAdapter(): StorageAdapter {
  if (typeof localStorage === "undefined") {
    return new MemoryStorageAdapter();
  }
  return {
    getItem: (k) => localStorage.getItem(k),
    setItem: (k, v) => localStorage.setItem(k, v),
    removeItem: (k) => localStorage.removeItem(k),
  };
}

export function serializeSave(state: GameState): string {
  const payload: PersistedSaveV1 = {
    schemaVersion: SAVE_SCHEMA_VERSION,
    game: state,
  };
  return JSON.stringify(payload);
}

/**
 * Load + validate + migrate. Corruption / unknown version → fresh state
 * (caller may still keep settings.testSpeed from URL).
 */
export function loadGameState(adapter: StorageAdapter): {
  state: GameState;
  recovered: boolean;
  reason?: string;
} {
  const raw = adapter.getItem(SAVE_STORAGE_KEY);
  if (raw == null || raw === "") {
    return { state: createInitialGameState(), recovered: false };
  }
  if (typeof raw !== "string" || raw.length > MAX_SAVE_JSON_CHARS) {
    return {
      state: createInitialGameState(),
      recovered: true,
      reason: "oversized or invalid save payload",
    };
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    const migrated = migrateSave(parsed);
    if (!migrated.ok) {
      return {
        state: createInitialGameState(),
        recovered: true,
        reason: migrated.reason,
      };
    }
    const { state: sanitized, recovered, reason } = sanitizeGameStateDeep(migrated.state);
    return { state: sanitized, recovered, reason };
  } catch (err) {
    return {
      state: createInitialGameState(),
      recovered: true,
      reason: err instanceof Error ? err.message : "corrupt save",
    };
  }
}

export function saveGameState(adapter: StorageAdapter, state: GameState): void {
  // Never persist session testSpeed — sanitize on write as defense in depth.
  const safe: GameState = {
    ...state,
    settings: { ...state.settings, testSpeed: false },
  };
  adapter.setItem(SAVE_STORAGE_KEY, serializeSave(safe));
}

export function clearGameState(adapter: StorageAdapter): void {
  adapter.removeItem(SAVE_STORAGE_KEY);
}

export function migrateSave(
  raw: unknown,
): { ok: true; state: GameState } | { ok: false; reason: string } {
  if (!raw || typeof raw !== "object") {
    return { ok: false, reason: "not an object" };
  }
  const obj = raw as Record<string, unknown>;
  const version = obj.schemaVersion;
  if (version !== SAVE_SCHEMA_VERSION) {
    return { ok: false, reason: `unsupported schemaVersion ${String(version)}` };
  }
  if (!obj.game || typeof obj.game !== "object") {
    return { ok: false, reason: "missing game" };
  }
  return { ok: true, state: obj.game as GameState };
}

/** Drop invalid drafts / edits / portfolio / lastPublish without crashing. */
export function sanitizeGameState(state: GameState): GameState {
  return sanitizeGameStateDeep(state).state;
}

export function sanitizeGameStateDeep(state: GameState): {
  state: GameState;
  recovered: boolean;
  reason?: string;
} {
  const base = createInitialGameState();
  const reasons: string[] = [];

  const unlocks = Array.isArray(state.unlocks)
    ? (state.unlocks.filter((u) => typeof u === "string" && KNOWN_UNLOCKS.has(u)) as UnlockId[])
    : [...STARTING_UNLOCKS];
  if (Array.isArray(state.unlocks) && unlocks.length !== state.unlocks.length) {
    reasons.push("dropped unknown unlock ids");
  }

  const purchases = Array.isArray(state.purchases)
    ? (state.purchases.filter((p) => typeof p === "string" && KNOWN_STORE.has(p)) as StoreItemId[])
    : [];
  if (Array.isArray(state.purchases) && purchases.length !== state.purchases.length) {
    reasons.push("dropped unknown store ids");
  }

  const equippedTheme = sanitizeEquippedTheme(
    purchases,
    (state as { equippedTheme?: unknown }).equippedTheme,
  );
  if (
    (state as { equippedTheme?: unknown }).equippedTheme !== undefined &&
    (state as { equippedTheme?: unknown }).equippedTheme !== equippedTheme
  ) {
    reasons.push("sanitized equippedTheme");
  }

  const completedStoryIds = Array.isArray(state.completedStoryIds)
    ? (state.completedStoryIds.filter((id) =>
        SHIFT_PACK_ORDER.includes(id as PackId),
      ) as PackId[])
    : [];

  let currentEdit = state.currentEdit ?? null;
  if (currentEdit && !isCurrentEditValid(currentEdit)) {
    currentEdit = null;
    reasons.push("dropped invalid currentEdit");
  }
  if (currentEdit) {
    // Additive progression fields — coerce to sane values so an old v1 save (or a
    // hand-tampered one) never carries a bad origin / non-string crowd id.
    const origin =
      currentEdit.origin === "roadmap" || currentEdit.origin === "campaign"
        ? currentEdit.origin
        : undefined;
    const chosenCrowdId =
      typeof currentEdit.chosenCrowdId === "string" ? currentEdit.chosenCrowdId : undefined;
    currentEdit = {
      packId: currentEdit.packId,
      packVersion: currentEdit.packVersion,
      seed: currentEdit.seed,
      graphJson: currentEdit.graphJson,
      ...(origin ? { origin } : {}),
      ...(chosenCrowdId ? { chosenCrowdId } : {}),
    };
  }

  const audienceProgress = sanitizeAudienceProgress(
    (state as { audienceProgress?: unknown }).audienceProgress,
  );

  const drafts = (state.drafts ?? []).filter((d) => {
    try {
      return isCurrentEditValid({
        packId: d.packId,
        packVersion: d.packVersion,
        seed: d.seed,
        graphJson: d.graphJson,
      });
    } catch {
      return false;
    }
  });
  if ((state.drafts ?? []).length !== drafts.length) {
    reasons.push("dropped invalid drafts");
  }

  const portfolio = (state.portfolio ?? []).filter((p) => isValidPortfolioRow(p));
  if ((state.portfolio ?? []).length !== portfolio.length) {
    reasons.push("dropped invalid portfolio rows");
  }

  let lastPublish = state.lastPublish ?? null;
  if (lastPublish != null && !isValidPublishSnapshot(lastPublish)) {
    lastPublish = null;
    reasons.push("dropped invalid lastPublish");
  }

  const economy = {
    reach: num(state.economy?.reach, 0),
    followers: num(state.economy?.followers, 0),
    cash: num(state.economy?.cash, 0),
    cred: num(state.economy?.cred, 0),
  };

  let screen: GameState["screen"] =
    isScreen(state.screen) &&
    state.screen !== "challenge-landing" &&
    state.screen !== "challenge-invalid"
      ? state.screen
      : "home";

  // Never land on the merged results (Compare) screen without a valid snapshot.
  if (screen === "compare" && !lastPublish) {
    screen = currentEdit ? "editor" : "home";
    reasons.push("recovered from results screen without snapshot");
  }

  const recovered = reasons.length > 0;
  return {
    state: {
      ...base,
      ...state,
      screen,
      storyIndex: clampStory(state.storyIndex),
      completedStoryIds,
      economy,
      unlocks: unlocks.length ? unlocks : [...STARTING_UNLOCKS],
      purchases,
      equippedTheme,
      drafts,
      portfolio,
      draftSlotLimit: num(state.draftSlotLimit, DEFAULT_DRAFT_SLOTS),
      portfolioSlotLimit: num(state.portfolioSlotLimit, DEFAULT_PORTFOLIO_SLOTS),
      settings: {
        // testSpeed is session/URL-only — never rehydrate from disk.
        testSpeed: false,
        homeIntroVersion: sanitizeHomeIntroVersion(
          (state.settings as { homeIntroVersion?: unknown } | undefined)?.homeIntroVersion,
        ),
      },
      currentEdit,
      lastPublish,
      publishRevealed: Boolean(state.publishRevealed) && Boolean(lastPublish),
      shiftComplete: Boolean(state.shiftComplete),
      audienceProgress,
      seq: num(state.seq, 0),
      qaBypass: Boolean(state.qaBypass),
      // Challenges are URL-ephemeral — never hydrate from localStorage.
      challenge: null,
    },
    recovered,
    reason: recovered ? reasons.join("; ") : undefined,
  };
}

function isValidRewards(r: unknown): r is PublishRewards {
  if (!r || typeof r !== "object") return false;
  const o = r as Record<string, unknown>;
  return (
    Number.isFinite(o.reachGain) &&
    Number.isFinite(o.followersGain) &&
    Number.isFinite(o.cashGain) &&
    Number.isFinite(o.credDelta)
  );
}

function isValidScore(s: unknown): boolean {
  if (!s || typeof s !== "object") return false;
  const o = s as Record<string, unknown>;
  return (
    Number.isFinite(o.reach) &&
    Number.isFinite(o.credDelta) &&
    Number.isFinite(o.contextLoss) &&
    Number.isFinite(o.claimDrift) &&
    Number.isFinite(o.provenanceRisk)
  );
}

function isValidPublishSnapshot(p: unknown): p is PublishSnapshot {
  if (!p || typeof p !== "object") return false;
  const o = p as Record<string, unknown>;
  if (!SHIFT_PACK_ORDER.includes(o.packId as PackId)) return false;
  if (typeof o.packVersion !== "string" || !o.packVersion) return false;
  if (typeof o.seed !== "number" || !Number.isFinite(o.seed)) return false;
  if (typeof o.graphJson !== "string" || o.graphJson.length < 2) return false;
  if (!isValidScore(o.score)) return false;
  if (!isValidRewards(o.rewards)) return false;
  if (!Array.isArray(o.comments)) return false;
  if (!o.compare || typeof o.compare !== "object") return false;

  const verified = verifyPersistedGraphAndScore({
    packId: o.packId as PackId,
    packVersion: o.packVersion,
    seed: o.seed as number,
    graphJson: o.graphJson as string,
    score: o.score as ScoreBreakdown,
  });
  return verified;
}

function isValidPortfolioRow(p: unknown): boolean {
  if (!p || typeof p !== "object") return false;
  const o = p as Record<string, unknown>;
  if (!SHIFT_PACK_ORDER.includes(o.packId as PackId)) return false;
  if (typeof o.packVersion !== "string" || !o.packVersion) return false;
  if (typeof o.seed !== "number" || !Number.isFinite(o.seed)) return false;
  if (typeof o.graphJson !== "string" || o.graphJson.length < 2) return false;
  if (!isValidScore(o.score)) return false;
  if (o.rewards != null && !isValidRewards(o.rewards)) return false;

  return verifyPersistedGraphAndScore({
    packId: o.packId as PackId,
    packVersion: o.packVersion,
    seed: o.seed as number,
    graphJson: o.graphJson as string,
    score: o.score as Pick<
      ScoreBreakdown,
      "reach" | "credDelta" | "contextLoss" | "claimDrift" | "provenanceRisk"
    >,
  });
}

/**
 * Deserialize/migrate graphJson, verify pack identity + seed, validate against
 * the current pack, and require stored score fields to match scoreEdit.
 * Intentionally does not import game UI modules — registry + @desk/core only.
 */
function verifyPersistedGraphAndScore(input: {
  packId: PackId;
  packVersion: string;
  seed: number;
  graphJson: string;
  score: Pick<
    ScoreBreakdown,
    "reach" | "credDelta" | "contextLoss" | "claimDrift" | "provenanceRisk"
  >;
}): boolean {
  const entry = PACK_REGISTRY[input.packId];
  if (!entry) return false;
  if (entry.pack.version !== input.packVersion) return false;
  try {
    const graph = deserializeEditGraph(input.graphJson);
    if (graph.seed !== input.seed) return false;
    if (graph.sourcePackId !== input.packId) return false;
    if (graph.sourcePackVersion !== input.packVersion) return false;
    if (graph.tickRate !== entry.pack.tickRate) return false;
    validateEditGraphAgainstPack(graph, entry.pack);
    const recomputed = scoreEdit(entry.pack, graph);
    return (
      recomputed.reach === input.score.reach &&
      recomputed.credDelta === input.score.credDelta &&
      recomputed.contextLoss === input.score.contextLoss &&
      recomputed.claimDrift === input.score.claimDrift &&
      recomputed.provenanceRisk === input.score.provenanceRisk
    );
  } catch {
    return false;
  }
}

function num(v: unknown, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) ? (v | 0) : fallback;
}

/**
 * True when `r` is a well-formed AudienceResult (all base fields present +
 * typed). The additive `graphJson` (Task 4) is optional — absent on old saves,
 * a string when present — and never marks the save as recovered.
 */
function isValidAudienceResult(r: unknown): r is AudienceResult {
  if (!r || typeof r !== "object") return false;
  const o = r as Record<string, unknown>;
  return (
    typeof o.crowdId === "string" &&
    typeof o.landed === "boolean" &&
    typeof o.clean === "boolean" &&
    Number.isFinite(o.reachMilli) &&
    Number.isFinite(o.credDeltaMilli) &&
    Number.isFinite(o.doubtMilli) &&
    typeof o.face === "string" &&
    typeof o.status === "string" &&
    (o.graphJson === undefined || typeof o.graphJson === "string") &&
    // Additive Job B banked-reward + fit fields — optional numbers on old saves.
    (o.fitMilli === undefined || Number.isFinite(o.fitMilli)) &&
    (o.rewardFollowers === undefined || Number.isFinite(o.rewardFollowers)) &&
    (o.rewardCash === undefined || Number.isFinite(o.rewardCash))
  );
}

/**
 * Coerce a validated result to a normalized shape: the additive Job B fields
 * (`fitMilli` / banked `rewardFollowers` / `rewardCash`) default to 0 when a
 * pre-Job-B save omits them, so a first replay on an old save pays the full best
 * (delta from a 0 bank — backward-compatible, acceptable). Never marks recovered.
 */
function normalizeAudienceResult(r: AudienceResult): AudienceResult {
  const out: AudienceResult = {
    ...r,
    fitMilli: Number.isFinite(r.fitMilli) ? (r.fitMilli as number) : 0,
    rewardFollowers: Number.isFinite(r.rewardFollowers) ? (r.rewardFollowers as number) : 0,
    rewardCash: Number.isFinite(r.rewardCash) ? (r.rewardCash as number) : 0,
  };
  return out;
}

/**
 * Default + validate the progression map. Missing / non-object → `{}` so an old
 * v1 save (which has no `audienceProgress`) loads cleanly. Only known pack ids
 * and well-formed results are kept. Never marked as recovered — additive fields
 * absent by design are not corruption.
 */
function sanitizeAudienceProgress(
  raw: unknown,
): GameState["audienceProgress"] {
  const out: GameState["audienceProgress"] = {};
  if (!raw || typeof raw !== "object") return out;
  const obj = raw as Record<string, unknown>;
  for (const packId of SHIFT_PACK_ORDER) {
    const forStory = obj[packId];
    if (!forStory || typeof forStory !== "object") continue;
    const clean: Record<string, AudienceResult> = {};
    for (const [crowdId, result] of Object.entries(forStory as Record<string, unknown>)) {
      if (isValidAudienceResult(result)) clean[crowdId] = normalizeAudienceResult(result);
    }
    if (Object.keys(clean).length > 0) out[packId] = clean;
  }
  return out;
}

/** Missing/invalid → 0; valid integers in [0, MAX_HOME_INTRO_VERSION] retained. */
export function sanitizeHomeIntroVersion(raw: unknown): number {
  if (typeof raw !== "number" || !Number.isFinite(raw) || !Number.isInteger(raw)) {
    return 0;
  }
  if (raw < 0 || raw > MAX_HOME_INTRO_VERSION) return 0;
  return raw;
}

function clampStory(n: unknown): number {
  const v = typeof n === "number" ? n | 0 : 0;
  if (v < 0) return 0;
  if (v > SHIFT_PACK_ORDER.length - 1) return SHIFT_PACK_ORDER.length - 1;
  return v;
}

function isScreen(s: unknown): s is GameState["screen"] {
  return (
    s === "home" ||
    s === "brief" ||
    s === "editor" ||
    s === "compare" ||
    s === "recap" ||
    s === "portfolio" ||
    s === "roadmap" ||
    s === "challenge-landing" ||
    s === "challenge-invalid"
  );
}
